import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState, useEffect } from "react";
import { DocumentPreview } from "./DocumentPreview";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { DocumentCard } from "./DocumentCard";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Grid3X3, List, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

interface Document {
  id: string;
  title: string;
  content: string | null;
  document_type: string;
  created_at: string;
  updated_at: string;
  case_id: string | null;
  user_id: string;
  is_favorite?: boolean;
  category?: string;
  version?: number;
  parent_document_id?: string | null;
  shared_with?: string[];
  last_modified_by?: string | null;
  version_description?: string | null;
  template_id?: string | null;
}

export interface DocumentListProps {
  documents: Document[];
  onDelete: (id: string) => void;
}

export function DocumentList({ documents, onDelete }: DocumentListProps) {
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [customCategory, setCustomCategory] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("alle");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Predefinerte kategorier
  const predefinedCategories = [
    "alle",
    "kontrakter",
    "avtaler",
    "korrespondanse",
    "rettslige_dokumenter",
    "personvern",
    "annet"
  ];

  const categoryLabels: { [key: string]: string } = {
    alle: "Alle dokumenter",
    kontrakter: "Kontrakter",
    avtaler: "Avtaler",
    korrespondanse: "Korrespondanse",
    rettslige_dokumenter: "Rettslige dokumenter",
    personvern: "Personvern",
    annet: "Annet"
  };

  // Filter documents based on selected category
  const filteredDocuments = selectedCategory === "alle" 
    ? documents 
    : documents.filter(doc => doc.category === selectedCategory);

  // Group documents by category
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const category = doc.category || 'ukategorisert';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(doc);
    return acc;
  }, {} as { [key: string]: Document[] });

  const toggleSection = (category: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!documents?.length) {
    return (
      <div className="text-center py-8 px-4 bg-white/50 rounded-lg border-2 border-dashed">
        <h3 className="font-medium text-lg mb-2">Ingen dokumenter ennå</h3>
        <p className="text-muted-foreground text-sm">
          Start med å opprette ditt første dokument ved å klikke på "Opprett"
        </p>
      </div>
    );
  }

  const handleView = (doc: Document) => {
    try {
      console.log("Viser dokument:", doc.id);
      setSelectedDoc(doc);
    } catch (error) {
      console.error('Feil ved visning av dokument:', error);
      toast({
        variant: "destructive",
        title: "Feil ved visning",
        description: "Kunne ikke vise dokumentet. Vennligst prøv igjen.",
      });
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const element = document.createElement("a");
      const file = new Blob([doc.content || ''], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);
      element.download = `${doc.title}.txt`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      toast({
        title: "Dokument lastet ned",
        description: `${doc.title} har blitt lastet ned`,
      });
    } catch (error) {
      console.error('Feil ved nedlasting av dokument:', error);
      toast({
        variant: "destructive",
        title: "Feil ved nedlasting",
        description: "Kunne ikke laste ned dokumentet. Vennligst prøv igjen.",
      });
    }
  };

  const handleShare = async (doc: Document) => {
    toast({
      title: "Kommer snart",
      description: "Delingsfunksjonalitet vil være tilgjengelig i neste oppdatering",
    });
  };

  const handleVersionHistory = async (doc: Document) => {
    toast({
      title: "Kommer snart",
      description: "Versjonshistorikk vil være tilgjengelig i neste oppdatering",
    });
  };

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Velg kategori" />
          </SelectTrigger>
          <SelectContent>
            {predefinedCategories.map((category) => (
              <SelectItem key={category} value={category}>
                {categoryLabels[category]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode('grid')}
            className={viewMode === 'grid' ? 'bg-primary/10' : ''}
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'bg-primary/10' : ''}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {Object.entries(groupedDocuments).map(([category, docs]) => (
          <motion.div
            key={category}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-white/50 rounded-lg border border-amber-100/50 overflow-hidden mb-4"
          >
            <Button
              variant="ghost"
              className="w-full flex justify-between items-center p-4"
              onClick={() => toggleSection(category)}
            >
              <span className="font-medium">
                {categoryLabels[category] || "Ukategorisert"}
              </span>
              {expandedSections[category] ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>

            <AnimatePresence>
              {expandedSections[category] && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className={viewMode === 'grid' 
                    ? "grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 p-4"
                    : "flex flex-col gap-4 p-4"
                  }>
                    {docs.map((doc) => (
                      <DocumentCard
                        key={doc.id}
                        document={doc}
                        onView={handleView}
                        onDownload={handleDownload}
                        onDelete={onDelete}
                        onShare={handleShare}
                        onVersionHistory={handleVersionHistory}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}